import { useSyncExternalStore } from 'react';
import ApiService from '../services/apiService';
import { IntegrationLinkContext } from '../types';
import { logger } from './logger';

/**
 * De afspraak waarvoor de gebruiker via "Start Notizy" bij een partner opneemt.
 *
 * Bewust alleen in het geheugen van de pagina: de recorder toont zolang een
 * balk "Opname voor …", en de eerstvolgende upload neemt de afspraak mee. Zodra
 * die upload een file_progress_id heeft, zet de recorder hem één keer als
 * voorgekozen exportbestemming. Lukt dat niet, dan kiest de medewerker de
 * afspraak zelf bij het exporteren.
 */
export interface PartnerRecordingTarget {
    configId: number;
    partnerName: string;
    context: IntegrationLinkContext & { ref: string };
}

type Listener = () => void;

let current: PartnerRecordingTarget | null = null;
const listeners = new Set<Listener>();

const emit = () => listeners.forEach(listener => listener());

export const setPartnerRecordingTarget = (target: PartnerRecordingTarget | null): void => {
    current = target;
    emit();
};

/** Wegklikken van de balk: de afspraak gaat dan niet mee. */
export const clearPartnerRecordingTarget = (): void => setPartnerRecordingTarget(null);

/** Voor de upload die de afspraak meeneemt; daarna is de balk weg. */
export const takePartnerRecordingTarget = (): PartnerRecordingTarget | null => {
    const target = current;
    if (target) {
        setPartnerRecordingTarget(null);
    }
    return target;
};

const subscribe = (listener: Listener) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const usePartnerRecordingTarget = (): PartnerRecordingTarget | null =>
    useSyncExternalStore(subscribe, () => current, () => null);

/** Fouten worden alleen gelogd: zonder voorgekozen afspraak werkt exporteren gewoon. */
export const applyPartnerRecordingTarget = async (fileProgressId: string, target: PartnerRecordingTarget): Promise<void> => {
    try {
        await new ApiService().setFileExportContext(fileProgressId, target.configId, target.context);
    } catch (error: any) {
        logger.warn('Voorgekozen afspraak bij opname zetten mislukt:', error?.status ?? error?.code ?? error);
    }
};
